/**
 * @classdesc The `MarketingStatus` class serves as the source of truth for current marketing statuses and their codes.
 */
export default class MarketingStatus {
  /**
   * Gets the instance for the ComingSoon marketing status.
   * @returns {MarketingStatus} The instance for the ComingSoon status.
   */
  static get ComingSoon() {
    return this.getInstance('C');
  }

  /**
   * Gets the instance for the Active marketing status.
   * @returns {MarketingStatus} The instance for the Active status.
   */
  static get Active() {
    return this.getInstance('N');
  }

  /**
   * Gets the instance for the GrandOpening marketing status.
   * @returns {MarketingStatus} The instance for the GrandOpening status.
   */
  static get GrandOpening() {
    return this.getInstance('G');
  }

  /**
   * Gets the instance for the CloseOut marketing status.
   * @returns {MarketingStatus} The instance for the CloseOut status.
   */
  static get CloseOut() {
    return this.getInstance('X');
  }

  /**
   * Gets the instance for the ComingSoonGrandOpening marketing status.
   * @returns {MarketingStatus} The instance for the ComingSoonGrandOpening status.
   */
  static get ComingSoonGrandOpening() {
    return this.getInstance('CG');
  }

  /**
   * Converts a key to the corresponding marketing status instance.
   * @param {string} key The key representing a marketing status.
   * @returns {MarketingStatus|null} The marketing status instance or null if not found.
   */
  static fromKey(key) {
    const statusMap = {
      C: MarketingStatus.ComingSoon,
      N: MarketingStatus.Active,
      G: MarketingStatus.GrandOpening,
      X: MarketingStatus.CloseOut,
      CG: MarketingStatus.ComingSoonGrandOpening,
    };
    return statusMap[key] || null;
  }

  /**
   * Gets or creates an instance of the `MarketingStatus` class based on the provided key.
   * @param {string} key The key representing a marketing status.
   * @returns {MarketingStatus} The marketing status instance.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new MarketingStatus(key);
    }
    return this.instances[key];
  }

  /**
   * Creates a new instance of the `MarketingStatus` class.
   * @param {string} name The name of the marketing status.
   */
  constructor(name) {
    this.name = name;
  }

  /**
   * Returns the name of the marketing status as a string.
   * @returns {string} The name of the marketing status.
   */
  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(MarketingStatus, 'instances', { value: {} });
